import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RefreshCw, Download, Mail, Phone, Building, FileText } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Admin Dashboard - Soshniti Legal';
  }, []);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const response = await fetch(`${API}/legal-requests`);
      const data = await response.json();
      setSubmissions(data);
      toast.success('Submissions loaded successfully');
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const exportToCSV = () => {
    const headers = ['Date', 'Name', 'Email', 'Phone', 'Company', 'Service Type', 'Message'];
    const csvData = submissions.map(sub => [
      new Date(sub.timestamp).toLocaleString(),
      sub.name,
      sub.email,
      sub.phone,
      sub.company || 'N/A',
      sub.serviceType,
      sub.message || 'N/A'
    ]);

    const csv = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `legal-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported successfully');
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />
      
      <div className="container mx-auto px-4 py-24">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">
                Form Submissions Dashboard
              </h1>
              <p className="text-gray-400">
                Total Submissions: {submissions.length}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={fetchSubmissions}
                variant="outline"
                className="border-slate-600 text-gray-300 hover:bg-slate-700"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button
                onClick={exportToCSV}
                className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
                disabled={submissions.length === 0}
              >
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
              <p className="text-gray-400 mt-4">Loading submissions...</p>
            </div>
          )}

          {/* No Submissions */}
          {!loading && submissions.length === 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-12 text-center">
                <p className="text-gray-400 text-lg">No submissions yet</p>
              </CardContent>
            </Card>
          )}

          {/* Submissions List */}
          {!loading && submissions.length > 0 && (
            <div className="space-y-4">
              {submissions.map((submission, index) => (
                <Card 
                  key={submission.id || index}
                  className="bg-slate-800/50 border-slate-700 hover:border-yellow-500/50 transition-all duration-300"
                >
                  <CardHeader className="border-b border-slate-700">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-white text-xl mb-2">
                          {submission.name}
                        </CardTitle>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <span className="bg-yellow-500/20 text-yellow-500 px-2 py-1 rounded">
                            {submission.serviceType}
                          </span>
                          <span>•</span>
                          <span>{new Date(submission.timestamp).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3 text-gray-300">
                        <Mail className="h-5 w-5 text-yellow-500" />
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <a href={`mailto:${submission.email}`} className="hover:text-yellow-500">
                            {submission.email}
                          </a>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 text-gray-300">
                        <Phone className="h-5 w-5 text-yellow-500" />
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <a href={`tel:${submission.phone}`} className="hover:text-yellow-500">
                            {submission.phone}
                          </a>
                        </div>
                      </div>
                      
                      {submission.company && (
                        <div className="flex items-center gap-3 text-gray-300">
                          <Building className="h-5 w-5 text-yellow-500" />
                          <div>
                            <p className="text-xs text-gray-500">Company</p>
                            <p>{submission.company}</p>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-3 text-gray-300">
                        <FileText className="h-5 w-5 text-yellow-500" />
                        <div>
                          <p className="text-xs text-gray-500">Service Type</p>
                          <p>{submission.serviceType}</p>
                        </div>
                      </div>
                    </div>
                    
                    {submission.message && (
                      <div className="mt-4 pt-4 border-t border-slate-700">
                        <p className="text-xs text-gray-500 mb-2">Message</p>
                        <p className="text-gray-300 leading-relaxed">{submission.message}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
